import { useEffect, useMemo, useRef } from "react";
import { createTapHold, HOLD_MS } from "./tapHold";

export { HOLD_MS };

export interface TapHoldHandlers {
  onPointerDown: (event: React.PointerEvent) => void;
  onPointerUp: (event: React.PointerEvent) => void;
  onPointerLeave: () => void;
  onPointerCancel: () => void;
  onContextMenu: (event: React.SyntheticEvent) => void;
}

/**
 * Tap seeks by thirty seconds; hold jumps to the previous or next stream. The behaviour
 * lives in {@link createTapHold}; this is the DOM wiring around it.
 */
export function useTapHold(onTap: () => void, onHold: () => void): TapHoldHandlers {
  // Read the callbacks through refs so the machine is built once. Rebuilding it on every
  // render would drop a hold timer mid-press whenever the parent re-rendered, which it does
  // continuously while audio is playing.
  const tapRef = useRef(onTap);
  const holdRef = useRef(onHold);
  tapRef.current = onTap;
  holdRef.current = onHold;

  const machine = useMemo(
    () =>
      createTapHold({
        onTap: () => tapRef.current(),
        onHold: () => holdRef.current(),
      }),
    [],
  );

  useEffect(() => machine.dispose, [machine]);

  return {
    onPointerDown: (event) => {
      // Only the primary button: a right-click should not seek.
      if (event.button !== 0) return;
      event.currentTarget.setPointerCapture?.(event.pointerId);
      machine.down();
    },
    onPointerUp: (event) => {
      if (event.button !== 0) return;
      machine.up();
    },
    onPointerLeave: () => machine.cancel(),
    onPointerCancel: () => machine.cancel(),
    // A long press on touch raises the context menu on top of the hold, which cancels the
    // pointer stream and makes the button look broken.
    onContextMenu: (event) => event.preventDefault(),
  };
}
